import React from 'react';
import { Typography, Tooltip } from '@mui/material';
import { DownloadRounded, OpenInNewRounded } from '@mui/icons-material';
import { EResource } from '../../../types/departamento';
import { IMiRecurso } from '../../../types/miRecurso';
import {
	StyledCard,
	StyledCardContent,
	FileIconBox,
	ContentBox,
	FileName,
	MetaText,
	FooterBox,
	DateText,
	DownloadButton,
} from './styles';

interface RecursoMateriaCardProps {
	recurso: IMiRecurso;
	docenteNombre: string;
	onDownload: (recurso: IMiRecurso) => void;
}

const getFileTypeLabel = (type: string): string => {
	const map: Record<string, string> = {
		pdf: 'PDF', docx: 'Word', pptx: 'PowerPoint',
		xlsx: 'Excel', video: 'Video', imagen: 'Imagen', other: 'Archivo', link: 'Enlace',
	};
	return map[type] ?? type.toUpperCase();
};

const formatRelativeDate = (date: Date): string => {
	const now = new Date();
	const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
	if (days === 0) return 'Hoy';
	if (days === 1) return 'Ayer';
	if (days < 7) return `Hace ${days} días`;
	if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
	if (days < 365) return `Hace ${Math.floor(days / 30)} meses`;
	return date.toLocaleDateString('es-MX');
};

const RecursoMateriaCard: React.FC<RecursoMateriaCardProps> = ({
	recurso,
	docenteNombre,
	onDownload,
}) => {
	return (
		<StyledCard>
			<StyledCardContent>
				<FileIconBox resourcetype={recurso.resourceType}>
					{recurso.resourceType === EResource.link
						? 'URL'
						: recurso.resourceType.toUpperCase().slice(0, 3)}
				</FileIconBox>

				<ContentBox>
					<FileName variant='subtitle1'>{recurso.name}</FileName>
					<MetaText variant='body2'>
						{getFileTypeLabel(recurso.resourceType)}
						{recurso.resourceType !== EResource.link && ` · ${recurso.size}`}
					</MetaText>
					<Typography variant='caption' color='text.secondary'>
						{docenteNombre}
					</Typography>
				</ContentBox>

				<FooterBox>
					<DateText variant='caption'>
						{formatRelativeDate(recurso.uploadDate)}
					</DateText>
					{recurso.resourceType === EResource.link && recurso.url ? (
						<Tooltip title='Abrir enlace' arrow>
							<DownloadButton
								size='small'
								onClick={() => window.open(recurso.url, '_blank', 'noopener,noreferrer')}
							>
								<OpenInNewRounded fontSize='small' />
							</DownloadButton>
						</Tooltip>
					) : (
						<Tooltip title='Descargar' arrow>
							<DownloadButton size='small' onClick={() => onDownload(recurso)}>
								<DownloadRounded fontSize='small' />
							</DownloadButton>
						</Tooltip>
					)}
				</FooterBox>
			</StyledCardContent>
		</StyledCard>
	);
};

export default RecursoMateriaCard;
